import { api } from '../_client';

export const filesJobsApi = {
  // Get count of CSV files for metadata re-extraction
  async getMetadataExtractionCount(): Promise<{
    success: boolean;
    total_csv_files: number;
    organization_name: string;
    error?: string;
  }> {
    const response = await api.post(
      '/api/files/rerun-metadata-extraction/count',
      {},
      {
        timeout: 30000, // 30 seconds timeout for counting
      }
    );
    return response.data;
  },

  // Start metadata extraction job (returns immediately with job_id)
  async startMetadataExtraction(globalScope: boolean = false): Promise<{
    success: boolean;
    job_id: string;
    status: string;
    message: string;
    error?: string;
  }> {
    try {
      const response = await api.post(
        '/api/files/rerun-metadata-extraction',
        { global_scope: globalScope },
        {
          timeout: 30000, // Short timeout since this returns immediately
        }
      );
      return response.data;
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        error.response.status === 409
      ) {
        throw new Error('Metadata extraction is already running. Please wait for it to complete.');
      }

      throw error;
    }
  },

  // Get status of metadata extraction job for polling
  async getExtractionJobStatus(jobId: string): Promise<{
    success: boolean;
    job_id: string;
    status: string;
    progress: {
      current: number;
      total: number;
      processed: number;
      failed: number;
      percentage: number;
    };
    created_at: string;
    updated_at: string;
    error_message?: string;
    result?: {
      success: boolean;
      message: string;
      total_files: number;
      processed_files: number;
      failed_files: Array<{ file: string; error: string }>;
      failed_count: number;
      duration_seconds: number;
      log_file: string;
      s3_modifications: {
        files_modified: number;
        description: string;
      };
    };
    log_file?: string;
  }> {
    try {
      const response = await api.get(`/api/files/extraction-jobs/${jobId}/status`, {
        timeout: 30000, // Increased timeout for job status during heavy processing
      });
      return response.data;
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response
      ) {
        const status = (error.response as { status?: number }).status;
        if (status === 404) {
          throw new Error('Job not found. It may have expired or been cleaned up.');
        } else if (status === 403) {
          throw new Error('Access denied. You can only view your own jobs.');
        }
      }

      throw error;
    }
  },

  // Get current extraction job for organization
  async getCurrentExtractionJob(): Promise<{
    success: boolean;
    has_current_job: boolean;
    job?: {
      job_id: string;
      status: string;
      progress: {
        current: number;
        total: number;
        processed: number;
        failed: number;
        percentage: number;
      };
      started_by: string;
      started_at: string;
      created_at: string;
      updated_at: string;
    };
  }> {
    const response = await api.get('/api/files/extraction-jobs/current', {
      timeout: 30000,
    });
    return response.data;
  },

  // Download extraction job log
  async downloadExtractionLog(jobId: string): Promise<Blob> {
    try {
      const response = await api.get(`/api/files/extraction-jobs/${jobId}/log/download`, {
        responseType: 'blob',
        timeout: 30000, // 30 seconds for log download
      });
      return response.data;
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response
      ) {
        const status = (error.response as { status?: number }).status;
        if (status === 404) {
          throw new Error(
            'Log file not found. It may have been cleaned up or the job may not have generated a log file yet.'
          );
        } else if (status === 403) {
          throw new Error('Access denied. You can only download logs for your own jobs.');
        }
      }

      throw error;
    }
  },
};
